import { Resource } from '@nextforce/api'
import { Icon, makeStyles, useTheme } from '@rneui/themed'
import { useLinkPreview } from 'chat/messages/queries/link_preview'
import { Image, ImageContentFit, ImageStyle } from 'expo-image'
import {
  isImageType,
  isPdfType,
  isVideoType,
} from 'file_folder_repository/util'
import { ColorValue, StyleProp, TextStyle, ViewStyle } from 'react-native'

interface Props {
  resource: Resource | undefined
  size?: number
  style?: ViewStyle | TextStyle
  color?: ColorValue
  externalLinkImageContentFit?: ImageContentFit
}

export function FileFolderIcon({
  resource,
  size,
  style,
  color,
  externalLinkImageContentFit = 'cover',
}: Props) {
  const styles = useStyles()
  const { theme } = useTheme()
  const isExternalFile =
    resource?.type === 'file' && resource.currentVersion?.externalUrl
  const mimeType =
    resource?.type === 'file' ? resource.currentVersion?.fileMimeType : 'folder'
  const { name, type, solid } = isExternalFile
    ? { name: 'file-link', type: 'material-design' }
    : getFileFolderIconNameType(mimeType)

  const externalLinkPreview = useLinkPreview(
    isExternalFile ? resource.currentVersion?.externalUrl : ''
  )

  const externalLinkImageSource = externalLinkPreview.data?.image

  return externalLinkImageSource ? (
    <Image
      source={{ uri: externalLinkImageSource }}
      style={[styles.externalLinkImage, style as StyleProp<ImageStyle>]}
      contentFit={externalLinkImageContentFit}
    />
  ) : (
    <Icon
      name={name}
      type={type}
      solid={solid}
      size={size}
      style={style as any}
      color={color ?? theme.colors.black}
    />
  )
}

function getFileFolderIconNameType(type: string | undefined): {
  name: string
  type: string
  solid?: boolean
} {
  if (isImageType(type)) {
    return { name: 'image', type: 'feather' }
  }
  if (isVideoType(type)) {
    return { name: 'video', type: 'feather' }
  }
  if (isPdfType(type)) {
    return { name: 'file-pdf', type: 'font-awesome-5', solid: true }
  }
  if (type === 'folder') {
    return { name: 'folder', type: 'feather' }
  }
  return { name: 'file', type: 'feather' }
}

const useStyles = makeStyles(theme => ({
  externalLinkImage: {
    width: 60,
    height: 60,
    borderRadius: 5,
  },
}))
